import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from "react";
import {
    ExpandedState,
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getFilteredRowModel,
    getExpandedRowModel,
    ColumnDef,
    flexRender,
} from '@tanstack/react-table'

import clsx from "clsx";

interface IProps {
    columns: ColumnDef<User>[];
    data: User[];
    isLoading: boolean;
    selectedChangeHandler?: Dispatch<SetStateAction<Array<any>>>;
    hiddenColumns?: any[];
    totalRows: number;
    classNameBody?: string;
    onSelectRow?: (rows: any[]) => void;
    onVisible?: boolean
}

export interface User {
    user_id: string
    user_full_name: string
    remaining_balance: string
    usage: string
    total: string
    period: string
    children?: User[]
    getRole?: () => void
}

const BroadcastTable: FC<IProps> = ({
    columns,
    data,
    isLoading,
    totalRows,
    classNameBody,
    hiddenColumns = [],
    selectedChangeHandler,
    onSelectRow,
    onVisible
}) => {
    const columnData = useMemo(() => columns, [columns]);
    const rowData = useMemo(() => data, [data]);
    const [expanded, setExpanded] = useState<ExpandedState>({})
    const [rowSelection, setRowSelection] = useState({})


    const obj: any = {}

    hiddenColumns.forEach((element) => {
        obj[element] = false;
    });


    const table = useReactTable<User>({
        data: rowData,
        columns: columnData,
        state: {
            expanded,
            columnVisibility: obj,
            rowSelection
        },
        enableRowSelection: true, //enable row selection for all rows
        onRowSelectionChange: setRowSelection,
        onExpandedChange: setExpanded,
        getSubRows: row => row.children,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getExpandedRowModel: getExpandedRowModel(),

        debugTable: true,
    })


    useEffect(() => {
        table.setPageSize(totalRows)
    }, [totalRows])

    useEffect(() => {
        if (selectedChangeHandler) selectedChangeHandler(rowData);
    }, [rowData]);

    useEffect(() => {
        const selectedRows = table.getSelectedRowModel().flatRows.map(row => row.original);
        if (onSelectRow) {
            onSelectRow(selectedRows);
        }
    }, [rowSelection])

    useEffect(() => {
        const row: any[] = []
        if (!onVisible) {
            setRowSelection({})
        }
    }, [onVisible])


    return (
        <>
            {isLoading ? (
                "loading"
            ) : (
                <div className="min-h-[200px] pb-[58px] border-b-gray-100">
                    <table className="w-full">
                        <thead className="sticky top-0 z-10 hover:text-gray-400" >
                            {table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id}
                                    className="text-gray-400 text-base font-bold py-4 hover:text-gray-200 bg-white border-gray-100 border-b">
                                    {headerGroup.headers.map(header => {
                                        return (
                                            <th key={header.id} colSpan={header.colSpan} className="hover:text-gray-400">
                                                {header.isPlaceholder ? null : (
                                                    <div className="flex gap-1 items-center py-2 cursor-pointer hover:text-gray-400">
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                    </div>
                                                )}
                                            </th>
                                        )
                                    })}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map(row => {
                                return (
                                    <tr
                                        className={clsx(
                                            "border relative border-b-gray-100 border-x-0 hover:bg-gray-100",
                                            classNameBody
                                        )}
                                        key={row.id}>
                                        {row.getVisibleCells().map(cell => {
                                            return (
                                                <td
                                                    className="whitespace-normal text-gray-700 text-md font-normal truncate"
                                                    key={cell.id}>
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
};

export default BroadcastTable;